import { IPaginationParams, IPaginationResponse } from '../models/interfaces/pagination';
import { DependencyList, useCallback, useState } from 'react';
import { useRetryableObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { delay, switchMap, tap } from 'rxjs/operators';

export default function usePaginationObservable<P extends IPaginationParams, T>(
  observableGenerator: (params: P) => Promise<IPaginationResponse<T>>,
  initialParams: Partial<P>,
  deps: DependencyList
) {
  const [lastResult, setLastResult] = useState<IPaginationResponse<T> | undefined>(undefined);
  const [params, setParams] = useState<P>({
    orderDirection: 'asc',
    pageSize: 10,
    page: 0,
    ...initialParams
  } as P);

  const mergeParams = useCallback(
    (newParams: Partial<P>, remove?: string[]) => {
      const p = { ...params };

      if (remove && remove.length > 0) remove.forEach(r => delete p[r]);

      setParams({ ...p, ...newParams });
    },
    [params]
  );

  const [data, error, completed, retry] = useRetryableObservable(() => {
    return of(true).pipe(
      delay(500),
      switchMap(() => observableGenerator(params)),
      tap(
        data => setLastResult(data),
        () => setLastResult(undefined)
      )
    );
  }, [params, ...deps]);

  return [
    params,
    mergeParams,
    data === undefined && error === undefined,
    (data || lastResult)?.['data'],
    error,
    completed,
    retry
  ] as const;
}
